import { message } from 'antd'
import { useCallback, useMemo, useState } from 'react'
import { DIGITAL_SIGN_STATUS } from '../../../../../constants'
import utils from '../../../../../utils'
import {
  SIGNED_FILE_BATCH_SIZE,
  splitIntoEvenSignChunks,
  SIGN_STEP_KEYS,
} from '../canvasDocConstants'
import { getSignedFileFromHistory } from '../utils/canvasDocFileHistory'
import { buildSignPayload } from '../utils/canvasDocSignPayload'

const buildLeaderMainFilesUpdate = (mainFiles, fileIndex, fileSignId) => {
  const updatedMainFiles = [...(mainFiles || [])]
  const currentFile = updatedMainFiles[fileIndex]
  const originalOriginId = currentFile.origin_file_id || currentFile.file_id

  updatedMainFiles[fileIndex] = {
    ...currentFile,
    file_id: fileSignId,
    origin_file_id: originalOriginId,
    leaderSigned: true,
    leader_signed: true,
    leadKtatSigned: false,
    bodSigned: false,
    digitalSigned: false,
    numberRegistered: false,
  }

  return updatedMainFiles
}

export const useCanvasDocSigning = ({
  code,
  main_files,
  action_history,
  signedDocumentStore,
  electronicDocumentStore,
  fileStore,
  loadingAnimationStore,
  clearFileView,
  refreshFileViewAtIndex,
}) => {
  const [selectedFileForAction, setSelectedFileForAction] = useState(null)
  const [selectedFileIndex, setSelectedFileIndex] = useState(null)

  const resetSignSelection = useCallback(() => {
    setSelectedFileForAction(null)
    setSelectedFileIndex(null)
  }, [])

  const getApprovedSignature = useCallback(async () => {
    const res = await signedDocumentStore.getUserSignatureList()
    const signatures = res.data || []
    return (
      signatures.find(s => s.status === DIGITAL_SIGN_STATUS.APPROVAL) ||
      signatures[0] ||
      null
    )
  }, [signedDocumentStore])

  const resolveSignedFileId = useCallback(
    (file, historyAction) => {
      const signedFile = getSignedFileFromHistory(
        action_history,
        file.file_id,
        historyAction
      )
      return signedFile?.file_id || file.file_id
    },
    [action_history]
  )

  const persistSignedFile = useCallback(
    async ({
      fileSignId,
      signStep,
      fileIndex,
      originalFile,
      includeMainFiles = false,
    }) => {
      const payload = {
        document_code: code,
        signed_file_index: fileIndex,
        original_file_id: originalFile?.file_id,
        new_file_id: fileSignId,
        sign_step: signStep,
      }

      if (includeMainFiles) {
        payload.main_files = buildLeaderMainFilesUpdate(
          main_files,
          fileIndex,
          fileSignId
        )
      }

      await electronicDocumentStore.updateSignedFile(payload)
      clearFileView()

      const { data } = await electronicDocumentStore.getElectronicDocumentById(
        code
      )
      await refreshFileViewAtIndex(fileIndex, data)
      return data
    },
    [
      code,
      clearFileView,
      electronicDocumentStore,
      main_files,
      refreshFileViewAtIndex,
    ]
  )

  const createAfterSignHandler = useCallback(
    (signStep, { includeMainFiles = false, logLabel } = {}) => (
      fileIndex,
      originalFile // ← curried
    ) => async fileSignId => {
      try {
        loadingAnimationStore.showSpinner(true)
        await persistSignedFile({
          fileSignId,
          signStep,
          fileIndex,
          originalFile,
          includeMainFiles,
        })
      } catch (err) {
        if (logLabel) console.error(`Error during ${logLabel}:`, err)
        message.error(err.vi || 'Có lỗi xảy ra!')
      } finally {
        loadingAnimationStore.showSpinner(false)
      }
    },
    [loadingAnimationStore, persistSignedFile]
  )

  const afterSignHandlers = useMemo(
    () => ({
      [SIGN_STEP_KEYS.LEADER_SIGN]: createAfterSignHandler(
        SIGN_STEP_KEYS.LEADER_SIGN,
        { includeMainFiles: true }
      ),
      [SIGN_STEP_KEYS.LEAD_KTAT_SIGN]: createAfterSignHandler(
        SIGN_STEP_KEYS.LEAD_KTAT_SIGN,
        { logLabel: 'Lead KTAT sign' }
      ),
      [SIGN_STEP_KEYS.BOD_SIGN]: createAfterSignHandler(
        SIGN_STEP_KEYS.BOD_SIGN,
        { logLabel: 'BOD sign' }
      ),
      [SIGN_STEP_KEYS.DIGITAL_SIGN]: createAfterSignHandler(
        SIGN_STEP_KEYS.DIGITAL_SIGN,
        { logLabel: 'Digital Sign' }
      ),
    }),
    [createAfterSignHandler]
  )

  const handleDirectSignForFile = useCallback(
    async (file, index, signConfig) => {
      if (utils.getExtensionFile(file.file_name) !== 'pdf') {
        message.info('Bạn chỉ được phép ký số file PDF')
        return
      }
      if (signConfig.isSigned(file)) {
        message.info(`File này đã ${signConfig.label.toLowerCase()} rồi!`)
        return
      }

      try {
        loadingAnimationStore.showSpinner(true)

        const signature = await getApprovedSignature()
        if (!signature) {
          message.error('Không tìm thấy chữ ký số. Vui lòng tạo chữ ký trước!')
          return
        }

        const fileIdToSign = resolveSignedFileId(file, signConfig.historyAction)
        // Chỉ lần ký đầu (LEADER_SIGN) mới chèn footer — tránh footer trùng khi ký lại
        const dataSubmit = buildSignPayload([fileIdToSign], signature, {
          addFooter: signConfig.signStep === SIGN_STEP_KEYS.LEADER_SIGN,
        })
        const res = await signedDocumentStore.signDocumentSavis(dataSubmit)
        const fileSignId = res.data?.listSignFile?.[0]?.fileSignId

        if (!fileSignId) {
          message.error('Ký số thất bại!')
          return
        }

        // ← Truyền index & file trực tiếp, không dùng state
        await signConfig.afterSign(index, file)(fileSignId)
        message.success('Ký số thành công!')
      } catch (err) {
        console.error('Error direct sign:', err)
        message.error(err?.vi || 'Ký số thất bại!')
      } finally {
        loadingAnimationStore.showSpinner(false)
      }
    },
    [
      getApprovedSignature,
      loadingAnimationStore,
      resolveSignedFileId,
      signedDocumentStore,
    ]
  )

  const handleBatchSignAllForStep = useCallback(
    async signConfig => {
      const unsignedFiles = (main_files || [])
        .map((file, index) => ({ file, index }))
        .filter(
          ({ file }) =>
            !signConfig.isSigned(file) &&
            utils.getExtensionFile(file.file_name) === 'pdf'
        )

      if (unsignedFiles.length === 0) {
        message.info('Tất cả file đã được ký!')
        return
      }

      try {
        loadingAnimationStore.showSpinner(true)

        const signature = await getApprovedSignature()
        if (!signature) {
          message.error('Không tìm thấy chữ ký số. Vui lòng tạo chữ ký trước!')
          return
        }

        const unsignedChunks = splitIntoEvenSignChunks(unsignedFiles)
        const signResults = []

        for (const unsignedChunk of unsignedChunks) {
          const chunkFilesToSign = unsignedChunk.map(({ file }) => ({
            fileId: resolveSignedFileId(file, signConfig.historyAction),
            originalFileId: file.file_id,
          }))

          const dataSubmit = buildSignPayload(
            chunkFilesToSign.map(f => f.fileId),
            signature,
            {
              addFooter: signConfig.signStep === SIGN_STEP_KEYS.LEADER_SIGN,
            }
          )
          const response = await signedDocumentStore.signDocumentSavis(
            dataSubmit
          )
          signResults.push({
            unsigned: unsignedChunk,
            filesToSign: chunkFilesToSign,
            response,
          })
        }

        const batchItems = []
        signResults.forEach(
          ({ unsigned, filesToSign: chunkFilesToSign, response }) => {
            const signedResults = response?.data?.listSignFile || []
            signedResults.forEach((signed, i) => {
              if (!signed?.fileSignId) return
              const { index } = unsigned[i]
              const { originalFileId } = chunkFilesToSign[i]
              batchItems.push({
                document_code: code,
                signed_file_index: index,
                original_file_id: originalFileId,
                new_file_id: signed.fileSignId,
                sign_step: signConfig.signStep,
              })
            })
          }
        )

        if (batchItems.length === 0) {
          message.error('Ký số thất bại!')
          return
        }

        if (batchItems.length < unsignedFiles.length) {
          message.warning(
            `Chỉ ký thành công ${batchItems.length}/${unsignedFiles.length} file. Vui lòng thử lại các file còn lại.`
          )
        }

        for (
          let offset = 0;
          offset < batchItems.length;
          offset += SIGNED_FILE_BATCH_SIZE
        ) {
          const chunk = batchItems.slice(
            offset,
            offset + SIGNED_FILE_BATCH_SIZE
          )
          await electronicDocumentStore.updateSignedFilesBatch(code, chunk)
        }

        clearFileView()
        await electronicDocumentStore.getElectronicDocumentById(code)
        message.success(`Ký số thành công ${batchItems.length} file!`)
      } catch (err) {
        console.error('Error batch sign:', err)
        message.error(err?.vi || 'Ký số thất bại!')
      } finally {
        loadingAnimationStore.showSpinner(false)
      }
    },
    [
      clearFileView,
      code,
      electronicDocumentStore,
      getApprovedSignature,
      loadingAnimationStore,
      main_files,
      resolveSignedFileId,
      signedDocumentStore,
    ]
  )

  return {
    selectedFileForAction,
    afterSignHandlers,
    handleDirectSignForFile,
    handleBatchSignAllForStep,
    getSignedFileFromHistory: useCallback(
      (originalFileId, action) =>
        getSignedFileFromHistory(action_history, originalFileId, action),
      [action_history]
    ),
  }
}
